import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Phone, MessageCircle, MapPin, Send, Loader2 } from "lucide-react";

const CONTACT = {
  phone: "+91 98319 97959",
  whatsapp: "919831997959",
  address: "17/1, Lakshmi Narayan Mukherjee Lane, Room No. 209, Opposite Binani Bhawan, Kolkata - 700006",
};

const cards = [
  { icon: Phone, title: "Direct Phone Call", value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}`, testId: "contact-card-phone" },
  { icon: MessageCircle, title: "WhatsApp Quick Chat", value: "Chat with us instantly", href: `https://wa.me/${CONTACT.whatsapp}`, testId: "contact-card-whatsapp" },
  { icon: MapPin, title: "Office Address", value: CONTACT.address, testId: "contact-card-address" },
];

const CN_TYPES = ["Full Truckload (FTL)", "Part Load (PTL)", "Warehousing", "Other"];

const initial = { name: "", phone: "", consignment_type: CN_TYPES[0], origin: "", destination: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!/^[\d+\-\s]{7,15}$/.test(form.phone.trim())) errs.phone = "Enter a valid phone number";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Inquiry sent! Our team will contact you shortly.");
      setForm(initial);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send inquiry");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-slate-50 border-0 rounded-md px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:bg-white transition-colors duration-200";

  return (
    <div className="py-16 px-6 lg:px-16 max-w-7xl mx-auto w-full" data-testid="contact-page">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Get In Touch</div>
      <h1 className="font-['Outfit'] mt-3 text-4xl sm:text-5xl font-black tracking-tight text-slate-900">Contact Us</h1>
      <p className="mt-4 text-base text-slate-600 max-w-xl">
        Request a quote or reach us directly — we respond fast.
      </p>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left: quick contact cards */}
        <div className="lg:col-span-2 space-y-4">
          {cards.map(({ icon: Icon, title, value, href, testId }) => {
            const Inner = (
              <div className="flex items-start gap-4 bg-white border border-slate-100 rounded-xl p-6 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)] hover:-translate-y-1 hover:shadow-[0_16px_32px_-12px_rgba(15,23,42,0.12)] transition-[transform,box-shadow] duration-300">
                <div className="w-11 h-11 rounded-md bg-orange-50 flex items-center justify-center text-orange-500 shrink-0">
                  <Icon size={22} />
                </div>
                <div>
                  <div className="font-['Outfit'] font-semibold text-slate-900">{title}</div>
                  <div className="mt-1 text-sm text-slate-600">{value}</div>
                </div>
              </div>
            );
            return href ? (
              <a key={title} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="block" data-testid={testId}>{Inner}</a>
            ) : (
              <div key={title} data-testid={testId}>{Inner}</div>
            );
          })}
        </div>

        {/* Right: inquiry form */}
        <form onSubmit={submit} className="lg:col-span-3 bg-white border border-slate-100 rounded-xl p-8 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)]" data-testid="inquiry-form">
          <h2 className="font-['Outfit'] text-lg font-bold text-slate-900">Request a Quote</h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Name *</label>
              <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="inquiry-name-input" />
              {errors.name && <div className="text-xs text-red-500 mt-1" data-testid="inquiry-name-error">{errors.name}</div>}
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Phone Number *</label>
              <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 XXXXX XXXXX" data-testid="inquiry-phone-input" />
              {errors.phone && <div className="text-xs text-red-500 mt-1" data-testid="inquiry-phone-error">{errors.phone}</div>}
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Consignment Type</label>
              <select className={inputCls} value={form.consignment_type} onChange={set("consignment_type")} data-testid="inquiry-type-select">
                {CN_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Origin</label>
              <input className={inputCls} value={form.origin} onChange={set("origin")} placeholder="Pickup city" data-testid="inquiry-origin-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Destination</label>
              <input className={inputCls} value={form.destination} onChange={set("destination")} placeholder="Delivery city" data-testid="inquiry-destination-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Message</label>
              <textarea className={`${inputCls} min-h-[110px] resize-y`} value={form.message} onChange={set("message")} placeholder="Goods details, weight, timeline…" data-testid="inquiry-message-input" />
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="inquiry-submit-btn"
            className="mt-6 inline-flex items-center gap-2 bg-orange-500 text-white font-semibold px-8 py-3.5 rounded-md shadow-lg shadow-orange-500/30 hover:bg-orange-600 disabled:opacity-60 transition-colors duration-200">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {busy ? "Sending…" : "Send Inquiry"}
          </button>
        </form>
      </div>
    </div>
  );
}
